import { createApi } from "@reduxjs/toolkit/dist/query/react"
import {
  RTK_SLICE_API_LIST,
  SUBSCRIPTION_BRAND_ACTIVITY_STATS_ENDPOINT,
  SUBSCRIPTION_CONFORMING_LIMIT_ENDPOINT,
  SUBSCRIPTION_EMAIL_LOG_ENDPOINT,
  SUBSCRIPTION_ENDPOINT,
  SUBSCRIPTION_EVENTS_ENDPOINT,
  SUBSCRIPTION_LO_ACTIVITY_STATS_ENDPOINT,
  SUBSCRIPTION_LOG_ENDPOINT,
  SUBSCRIPTION_ORG_ACTIVITY_STATS_ENDPOINT,
  SUBSCRIPTION_PRINT_EMAIL_ENDPOINT,
  SUBSCRIPTION_QUOTE_ENDPOINT,
  SUBSCRIPTION_QUOTES_ENDPOINT,
  SUBSCRIPTION_TAXES_INSURANCE_ENDPOINT,
  SUBSCRIPTIONS_LOG_ENDPOINT
} from "../../common/constants"
import {
  getQueryString,
  convertPayloadToSubscriptionDetails
} from "../../common/utils"
import {
  CreateSubscriptionPayload,
  PaginatedSubscriptionLogsResponse,
  SubscriptionActivityStat,
  SubscriptionConformingLimitResponse,
  SubscriptionDetails,
  SubscriptionEmailLog,
  SubscriptionEmailStatsType,
  SubscriptionEvents,
  SubscriptionLog,
  SubscriptionQuotesResponse,
  TaxesAndInsuranceResponse,
  UpdateSubscriptionPayload
} from "../../types"
import { baseQuery, getTags } from "../api"
import bbApi from "../../api"

const reducerPath = "subscriptions"
const subscriptionDetails = "subscription-details"
const subscriptionDetailsByLeadPath = "subscription-details-by-lead"

export const subscriptionsApi = createApi({
  reducerPath,
  baseQuery,
  tagTypes: [reducerPath, subscriptionDetails, subscriptionDetailsByLeadPath],
  endpoints: builder => ({
    fetchSubscriptionMatches: builder.query({
      query: (params: { email?: string; phone?: string; brand_id?: string }) =>
        `${SUBSCRIPTION_ENDPOINT}${getQueryString(params)}`,
      transformResponse: (
        responseData: SubscriptionDetails | SubscriptionDetails[]
      ): SubscriptionDetails[] =>
        Array.isArray(responseData)
          ? responseData
          : responseData
          ? [responseData]
          : []
    }),
    fetchSubscriptionLogs: builder.query({
      query: (params: {
        offset: number
        limit: number
        loid?: string
        org_id?: string
        min_created?: number
        max_created?: number
      }) => `${SUBSCRIPTION_LOG_ENDPOINT}${getQueryString(params)}`,
      transformResponse: (responseData: SubscriptionLog[]) =>
        responseData || [],
      providesTags: result => getTags(reducerPath, "id", result)
    }),
    fetchSubscriptionLogsV2: builder.query({
      query: (params: {
        page: number
        page_size: number
        search?: string
        active?: number
        deleted?: number
        loid?: string
        org_id?: string
        min_created?: number
        max_created?: number
      }) => `${SUBSCRIPTIONS_LOG_ENDPOINT}${getQueryString(params)}`,
      transformResponse: (responseData: PaginatedSubscriptionLogsResponse) =>
        responseData,
      providesTags: result => getTags(reducerPath, "id", result?.data ?? [])
    }),
    fetchSubscriptionDetails: builder.query({
      query: (params: {
        id?: string
        email?: string
        phone?: string
        brand_id?: string
      }) => `${SUBSCRIPTION_ENDPOINT}${getQueryString(params)}`,
      transformResponse: (
        responseData: SubscriptionDetails | SubscriptionDetails[]
      ) => (Array.isArray(responseData) ? responseData[0] : responseData),
      providesTags: result =>
        getTags(subscriptionDetails, "id", result ? [result] : [])
    }),
    fetchSubscriptionsByBrand: builder.query({
      query: (brandId: string) =>
        `${SUBSCRIPTION_ENDPOINT}${getQueryString({ brand_id: brandId })}`,
      transformResponse: (responseData: SubscriptionDetails[]) =>
        responseData || []
    }),
    fetchSubscriptionsByLeadId: builder.query({
      query: (params: { lead_id: string }) =>
        `${SUBSCRIPTION_ENDPOINT}${getQueryString(params)}`,
      transformResponse: (responseData: SubscriptionDetails[]) =>
        responseData || [],
      providesTags: result =>
        getTags(subscriptionDetailsByLeadPath, "id", result ?? [])
    }),
    fetchSubscriptionEmailLogs: builder.query({
      query: (params: { id: string; type?: SubscriptionEmailStatsType }) =>
        `${SUBSCRIPTION_EMAIL_LOG_ENDPOINT}${getQueryString(params)}`,
      transformResponse: (responseData: { data: SubscriptionEmailLog[] }) =>
        responseData?.data || []
    }),
    fetchSubscriptionEvents: builder.query({
      query: (params: { subscription_id: string }) =>
        `${SUBSCRIPTION_EVENTS_ENDPOINT}${getQueryString(params)}`,
      transformResponse: (responseData: { data: SubscriptionEvents }) =>
        responseData?.data || []
    }),
    fetchSubscriptionQuote: builder.query({
      query: (params: { id: string; bb_sid: string }) =>
        `${SUBSCRIPTION_QUOTE_ENDPOINT}${getQueryString(params)}`,
      transformResponse: (responseData: { data: any[] }) =>
        responseData?.data || []
    }),
    fetchSubscriptionQuotes: builder.query({
      query: (params: {
        subscription_id: string
        timestamp_min?: number
        timestamp_max?: number
      }) => `${SUBSCRIPTION_QUOTES_ENDPOINT}${getQueryString(params)}`,
      transformResponse: (responseData: SubscriptionQuotesResponse) =>
        responseData
    }),
    fetchConformingLimit: builder.query({
      query: (params: { zipcode: string }) =>
        `${SUBSCRIPTION_CONFORMING_LIMIT_ENDPOINT}${getQueryString(params)}`,
      transformResponse: (responseData: SubscriptionConformingLimitResponse) =>
        responseData
    }),
    fetchTaxesAndInsurance: builder.query({
      query: (params: { zipcode?: string; list_price?: string }) =>
        `${SUBSCRIPTION_TAXES_INSURANCE_ENDPOINT}${getQueryString(params)}`,
      transformResponse: (responseData: TaxesAndInsuranceResponse) =>
        responseData
    }),
    createSubscription: builder.mutation({
      queryFn: async (data: CreateSubscriptionPayload) => {
        try {
          const payload = convertPayloadToSubscriptionDetails(data)
          const response = await bbApi.subscriptions.add(payload)
          console.log("createSubscription response", response)
          return { data: response }
        } catch (error) {
          throw error
        }
      },
      invalidatesTags: [{ type: reducerPath, id: RTK_SLICE_API_LIST }]
    }),
    updateSubscription: builder.mutation({
      queryFn: async (data: UpdateSubscriptionPayload) => {
        const response = await bbApi.subscriptions.update({
          ...convertPayloadToSubscriptionDetails(data),
          id: data.id
        })
        return { data: response }
      },
      invalidatesTags: [
        { type: subscriptionDetailsByLeadPath, id: RTK_SLICE_API_LIST },
        { type: subscriptionDetails, id: RTK_SLICE_API_LIST },
        { type: reducerPath, id: RTK_SLICE_API_LIST }
      ]
    }),
    sendIntroEmail: builder.mutation({
      queryFn: async (id: string) => {
        const response = await bbApi.subscriptions.sendIntroEmail(id)
        return response
      }
    }),
    fetchSubscriptionActivityStats: builder.query({
      query: (params: {
        event_range: number
        sort_by: string
        org_id?: string | undefined
        brand_id?: string | undefined
        loid?: string | undefined
      }) => {
        const { org_id, brand_id } = params
        if (org_id) {
          return `${SUBSCRIPTION_ORG_ACTIVITY_STATS_ENDPOINT}${getQueryString(
            params
          )}`
        } else if (brand_id) {
          return `${SUBSCRIPTION_BRAND_ACTIVITY_STATS_ENDPOINT}${getQueryString(
            params
          )}`
        } else {
          return `${SUBSCRIPTION_LO_ACTIVITY_STATS_ENDPOINT}${getQueryString(
            params
          )}`
        }
      },
      transformResponse: (responseData: { data: SubscriptionActivityStat[] }) =>
        responseData?.data || []
    }),
    fetchPrintEmail: builder.query({
      query: (params: { id: string }) => ({
        url: `${SUBSCRIPTION_PRINT_EMAIL_ENDPOINT}${getQueryString(params)}`,
        responseHandler: "text"
      }),
      transformResponse: (responseData: string) => responseData
    })
  })
})

export const {
  useFetchSubscriptionMatchesQuery,
  useLazyFetchSubscriptionMatchesQuery,
  useFetchSubscriptionLogsQuery,
  useFetchSubscriptionLogsV2Query,
  useFetchSubscriptionDetailsQuery,
  useLazyFetchSubscriptionDetailsQuery,
  useFetchSubscriptionsByBrandQuery,
  useFetchSubscriptionsByLeadIdQuery,
  useFetchSubscriptionEmailLogsQuery,
  useFetchSubscriptionEventsQuery,
  useFetchSubscriptionQuoteQuery,
  useFetchSubscriptionQuotesQuery,
  useFetchConformingLimitQuery,
  useSendIntroEmailMutation,
  useCreateSubscriptionMutation,
  useUpdateSubscriptionMutation,
  useFetchTaxesAndInsuranceQuery,
  useFetchSubscriptionActivityStatsQuery,
  useFetchPrintEmailQuery
} = subscriptionsApi
